import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  HiArrowLeft,
  HiPlus,
  HiXMark,
  HiSquares2X2,
  HiPencil,
  HiTrash,
  HiTrophy,
  HiCog6Tooth,
} from "react-icons/hi2";
import { userService } from "../../services/userService";
import { UserData } from "../../types/user";
import {
  ActivityType,
  ActivityVariableDefinition,
  TrainingType,
} from "../../types/activity";
import { CustomTrainingType } from "../../types/user";
import { PERIODISATION_OPTIONS } from "../../types/post";
import {
  resolvePeriodisationColor,
  resolveTrainingTypeColor,
} from "../../utils/scheduleColors";
import Header from "../../components/layout/Header";
import { activityService } from "../../services/activityService";
import { trainingTypeService } from "../../services/trainingTypeService";
import { getUnitsForVariable, getDefaultUnit } from "../../utils/unitUtils";
import { HiChevronRight } from "react-icons/hi2";
import { GlobalActivityService } from "../../services/globalActivityService";
import { GlobalActivityCategory } from "../../types/activity";
const AthletePreferences: React.FC = () => {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSports, setSelectedSports] = useState<string[]>([]);

  // Activity Types management state
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [isAddingActivityType, setIsAddingActivityType] =
    useState<boolean>(false);
  const [editingActivityType, setEditingActivityType] =
    useState<ActivityType | null>(null);
  const [activityTypeError, setActivityTypeError] = useState<string | null>(
    null,
  );
  const [savingActivityTypes, setSavingActivityTypes] =
    useState<boolean>(false);
  const [activityTypeToDelete, setActivityTypeToDelete] =
    useState<ActivityType | null>(null);

  const [newActivityType, setNewActivityType] = useState<{
    name: string;
    description: string;
    variables: ActivityVariableDefinition[]; // Support variables with units
    image?: string;
  }>({
    name: "",
    description: "",
    variables: [],
    image: "",
  });

  // Sport selection state
  const [availableSports, setAvailableSports] = useState<
    Record<string, string>
  >({});
  const [loadingSports, setLoadingSports] = useState<boolean>(true);
  const [savingSport, setSavingSport] = useState<boolean>(false);

  // Simple wellness setting state
  const [savingWellnessSetting, setSavingWellnessSetting] =
    useState<boolean>(false);

  // Casual mode setting state
  const [savingCasualMode, setSavingCasualMode] = useState<boolean>(false);

  // Hide peak score / wellness+sleep setting state
  const [savingHidePeakScore, setSavingHidePeakScore] =
    useState<boolean>(false);
  const [savingHideWellnessSleep, setSavingHideWellnessSleep] =
    useState<boolean>(false);

  // Sport request dialog state
  const [showSportRequestDialog, setShowSportRequestDialog] =
    useState<boolean>(false);

  // Sports popup state
  const [sportPopupOpen, setSportPopupOpen] = useState<boolean>(false);

  // Custom periodisation types state
  const [isAddingCustomPeriodisation, setIsAddingCustomPeriodisation] =
    useState<boolean>(false);
  const [newCustomPeriodisationName, setNewCustomPeriodisationName] =
    useState<string>("");
  const [customPeriodisationError, setCustomPeriodisationError] = useState<
    string | null
  >(null);
  const [savingCustomPeriodisation, setSavingCustomPeriodisation] =
    useState<boolean>(false);
  const [customPeriodisationToDelete, setCustomPeriodisationToDelete] =
    useState<string | null>(null);

  // Custom training types state
  const [catalogueTrainingTypes, setCatalogueTrainingTypes] = useState<
    TrainingType[]
  >([]);
  const [isAddingCustomTrainingType, setIsAddingCustomTrainingType] =
    useState<boolean>(false);
  const [newCustomTrainingTypeName, setNewCustomTrainingTypeName] =
    useState<string>("");
  const [newCustomTrainingTypeBaseID, setNewCustomTrainingTypeBaseID] =
    useState<string>("");
  const [customTrainingTypeError, setCustomTrainingTypeError] = useState<
    string | null
  >(null);
  const [savingCustomTrainingTypes, setSavingCustomTrainingTypes] =
    useState<boolean>(false);
  const [customTrainingTypeToDelete, setCustomTrainingTypeToDelete] =
    useState<CustomTrainingType | null>(null);
 // Activity Packs state
  const [globalCategories, setGlobalCategories] = useState<GlobalActivityCategory[]>([]);
  const [loadingGlobalCategories, setLoadingGlobalCategories] = useState<boolean>(true);
  const [togglingPackId, setTogglingPackId] = useState<string | null>(null);
  const [packToggleError, setPackToggleError] = useState<string | null>(null);
  const [addingAllPacks, setAddingAllPacks] = useState<boolean>(false);
  useEffect(() => {
  const fetchGlobalCategories = async () => {
    try {
      setLoadingGlobalCategories(true);
      const categories = await GlobalActivityService.fetchCategories();
      setGlobalCategories(categories);
    } catch (error) {
      console.error("Error fetching activity pack categories:", error);
    } finally {
      setLoadingGlobalCategories(false);
    }
  };
  fetchGlobalCategories();
}, []);
  useEffect(() => {
    if (userData?.trainingSportIDs) {
      setSelectedSports(userData.trainingSportIDs);
    }
  }, [userData]);

  // Fetch available sports from the sports collection
  const fetchAvailableSports = async () => {
    try {
      const allSports = await trainingTypeService.fetchSports();
      const sorted = [...allSports].sort((a, b) => {
        if (a.name === "General Fitness") return -1;
        if (b.name === "General Fitness") return 1;
        return a.name.localeCompare(b.name);
      });
      const sportsMap = sorted.reduce(
        (acc: Record<string, string>, sport) => {
          if (sport.id) acc[sport.id] = sport.name;
          return acc;
        },
        {},
      );
      setAvailableSports(sportsMap);
    } catch (error) {
      console.error("Error fetching available sports:", error);
      setAvailableSports({});
    } finally {
      setLoadingSports(false);
    }
  };
 const handleAddAllPacks = async () => {
  if (!currentUser?.uid) return;

  const current = userData?.adminActivityCatIDs || [];
  const allCategoryIds = globalCategories
    .map((c) => c.id)
    .filter((id): id is string => !!id);

  // Nothing new to add
  const alreadyHasAll = allCategoryIds.every((id) => current.includes(id));
  if (alreadyHasAll) return;

  const merged = Array.from(new Set([...current, ...allCategoryIds]));

  try {
    setAddingAllPacks(true);
    setPackToggleError(null);
    await userService.updateUserProfile(currentUser.uid, {
      adminActivityCatIDs: merged,
    });
    await refreshUserData();
    if (userData) {
      setUserData({ ...userData, adminActivityCatIDs: merged });
    }
  } catch (error) {
    console.error("Error adding all activity packs:", error);
    setPackToggleError(
      error instanceof Error
        ? error.message
        : "Failed to add all activity packs",
    );
  } finally {
    setAddingAllPacks(false);
  }
}; 
const handleTogglePack = async (categoryId: string) => {
  if (!currentUser?.uid) return;

  const current = userData?.adminActivityCatIDs || [];
  const isAdded = current.includes(categoryId);
  const updated = isAdded
    ? current.filter((id) => id !== categoryId)
    : [...current, categoryId];

  try {
    setTogglingPackId(categoryId);
    setPackToggleError(null);
    await userService.updateUserProfile(currentUser.uid, {
      adminActivityCatIDs: updated,
    });
    await refreshUserData();
    if (userData) {
      setUserData({ ...userData, adminActivityCatIDs: updated });
    }
  } catch (error) {
    console.error("Error toggling activity pack:", error);
    setPackToggleError(
      error instanceof Error ? error.message : "Failed to update activity pack",
    );
  } finally {
    setTogglingPackId(null);
  }
};
  // Fetch user data
  const fetchData = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { userData } = await userService.getUserProfile(currentUser.uid);
      setUserData(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch user data",
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's activity types
  const fetchActivityTypes = async () => {
    if (!currentUser?.uid) return;

    try {
      const typesData = await activityService.fetchActivityTypes(
        currentUser.uid,
      );
      setActivityTypes(typesData);
    } catch (error) {
      console.error("Error fetching activity types:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchActivityTypes();
    fetchAvailableSports();
  }, [currentUser?.uid]);

  // Refetch data when the page becomes visible again (e.g., returning from another page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser?.uid) {
        fetchData();
      }
    };

    const handleFocus = () => {
      if (currentUser?.uid) {
        fetchData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentUser?.uid]);

  // Activity Type CRUD Operations
  const resetActivityTypeForm = () => {
    setNewActivityType({
      name: "",
      description: "",
      variables: [],
      image: "",
    });
    setActivityTypeError(null);
    setIsAddingActivityType(false);
    setEditingActivityType(null);
  };

  const handleAddActivityType = async () => {
    if (!currentUser?.uid) return;

    if (newActivityType.variables.length === 0) {
      setActivityTypeError("Please select at least one variable");
      return;
    }

    try {
      setSavingActivityTypes(true);
      setActivityTypeError(null);

      const activityTypeData = {
        id: editingActivityType?.id,
        name: newActivityType.name,
        description: newActivityType.description,
        variables: newActivityType.variables, // Now ActivityVariableDefinition[]
        image: newActivityType.image,
        createdAt: editingActivityType?.createdAt,
      };

      await activityService.saveActivityType(currentUser.uid, activityTypeData);
      await fetchActivityTypes();
      resetActivityTypeForm();
    } catch (error) {
      console.error("Error saving activity type:", error);
      setActivityTypeError(
        error instanceof Error
          ? error.message
          : "Failed to save exercise. Please try again.",
      );
    } finally {
      setSavingActivityTypes(false);
    }
  };

  const handleEditActivityType = (activityType: ActivityType) => {
    setEditingActivityType(activityType);

    // Normalize variables to ActivityVariableDefinition[] format
    const normalizedVariables: ActivityVariableDefinition[] = (
      activityType.variables || []
    ).map((v) => {
      if (typeof v === "string") {
        // Legacy format - convert to new format
        return { name: v, defaultUnit: getDefaultUnit(v) };
      }
      // Already in new format
      return v;
    });

    setNewActivityType({
      name: activityType.name,
      description: activityType.description || "", // Handle legacy data without description
      variables: normalizedVariables, // Handle legacy data without variables
      image: activityType.image || "",
    });
    setIsAddingActivityType(true);
  };

  const handleDeleteActivityType = async (activityType: ActivityType) => {
    if (!currentUser?.uid || !activityType.id) return;

    try {
      setSavingActivityTypes(true);
      await activityService.deleteActivityType(
        currentUser.uid,
        activityType.id,
      );
      await fetchActivityTypes();
      setActivityTypeToDelete(null);
    } catch (error) {
      console.error("Error deleting activity type:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to delete exercise. Please try again.",
      );
    } finally {
      setSavingActivityTypes(false);
    }
  };

  const handleActivityTypeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAddActivityType();
  };

  // Available ActivityVariable names
  const AVAILABLE_VARIABLES: string[] = [
    "Distance",
    "Weight",
    "Intensity",
    "Speed",
    "Reps",
    "Time",
    "Other",
  ];

  const toggleVariable = (variableName: string, unit?: string) => {
    setNewActivityType((prev) => {
      const existingIndex = prev.variables.findIndex(
        (v) => v.name === variableName,
      );

      if (existingIndex !== -1) {
        // Remove if already exists
        return {
          ...prev,
          variables: prev.variables.filter((_, idx) => idx !== existingIndex),
        };
      } else {
        // Add with default unit if not provided
        const defaultUnit = unit || getDefaultUnit(variableName);
        return {
          ...prev,
          variables: [...prev.variables, { name: variableName, defaultUnit }],
        };
      }
    });
    setActivityTypeError(null);
  };

  const updateVariableUnit = (variableName: string, newUnit: string) => {
    setNewActivityType((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.name === variableName ? { ...v, defaultUnit: newUnit } : v,
      ),
    }));
  };

  const isVariableSelected = (variableName: string): boolean => {
    return newActivityType.variables.some((v) => v.name === variableName);
  };

  const getVariableUnit = (variableName: string): string => {
    const variable = newActivityType.variables.find(
      (v) => v.name === variableName,
    );
    return variable?.defaultUnit || getDefaultUnit(variableName);
  };

  // Handle training sport selection
  const handleTrainingSportChange = async (selectedIds: string[]) => {
    if (!currentUser?.uid) return;

    // Validation: Must select at least one sport
    if (selectedIds.length === 0) {
      setError("Please select at least one sport.");
      return;
    }

    try {
      setSavingSport(true);
      setError(null);

      await userService.updateUserProfile(currentUser.uid, {
        trainingSportIDs: selectedIds,
      });

      await refreshUserData();

      if (userData) {
        setUserData({ ...userData, trainingSportIDs: selectedIds });
      }
    } catch (error) {
      console.error("Error updating sports:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update sports",
      );
    } finally {
      setSavingSport(false);
    }
  };

  // Handle simple wellness setting toggle
  const handleSimpleWellnessSettingChange = async (enabled: boolean) => {
    if (!currentUser?.uid) return;

    try {
      setSavingWellnessSetting(true);
      await userService.updateUserProfile(currentUser.uid, {
        simpleWellnessSetting: enabled,
      });

      // Refresh user data
      await refreshUserData();

      // Update local state
      if (userData) {
        setUserData({ ...userData, simpleWellnessSetting: enabled });
      }
    } catch (error) {
      console.error("Error updating simple wellness setting:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to update simple wellness setting",
      );
    } finally {
      setSavingWellnessSetting(false);
    }
  };

  // Handle casual mode toggle
  const handleCasualModeChange = async (enabled: boolean) => {
    if (!currentUser?.uid) return;

    try {
      setSavingCasualMode(true);
      await userService.updateUserProfile(currentUser.uid, {
        casualMode: enabled,
      });

      // Refresh user data
      await refreshUserData();

      // Update local state
      if (userData) {
        setUserData({ ...userData, casualMode: enabled });
      }
    } catch (error) {
      console.error("Error updating casual mode:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update casual mode",
      );
    } finally {
      setSavingCasualMode(false);
    }
  };

  // Handle hide peak score toggle
  const handleHidePeakScoreChange = async (enabled: boolean) => {
    if (!currentUser?.uid) return;

    try {
      setSavingHidePeakScore(true);
      await userService.updateUserProfile(currentUser.uid, {
        hidePeakScore: enabled,
      });

      await refreshUserData();

      if (userData) {
        setUserData({ ...userData, hidePeakScore: enabled });
      }
    } catch (error) {
      console.error("Error updating hide peak score:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to update hide peak score",
      );
    } finally {
      setSavingHidePeakScore(false);
    }
  };

  // Handle hide wellness and sleep toggle
  const handleHideWellnessSleepChange = async (enabled: boolean) => {
    if (!currentUser?.uid) return;

    try {
      setSavingHideWellnessSleep(true);
      await userService.updateUserProfile(currentUser.uid, {
        hideWellnessSleep: enabled,
      });

      await refreshUserData();

      if (userData) {
        setUserData({ ...userData, hideWellnessSleep: enabled });
      }
    } catch (error) {
      console.error("Error updating hide wellness and sleep:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to update hide wellness and sleep",
      );
    } finally {
      setSavingHideWellnessSleep(false);
    }
  };

  // ─── Schedule colour preferences ───────────────────────────────────
  const [savingColors, setSavingColors] = useState<boolean>(false);

  const updateColorPreference = async (
    field: "trainingTypeColors" | "periodisationColors",
    key: string,
    hex: string,
  ) => {
    if (!currentUser?.uid || !userData) return;
    const nextMap = { ...(userData[field] || {}), [key]: hex };
    try {
      setSavingColors(true);
      await userService.updateUserProfile(currentUser.uid, { [field]: nextMap });
      await refreshUserData();
      setUserData({ ...userData, [field]: nextMap });
    } catch (error) {
      console.error("Error updating schedule colours:", error);
    } finally {
      setSavingColors(false);
    }
  };

  // ─── Custom Periodisation Types handlers ───────────────────────────
  const updateCustomPeriodisationInFirebase = async (
    updatedTypes: string[],
  ) => {
    if (!currentUser?.uid) return;
    try {
      setSavingCustomPeriodisation(true);
      await userService.updateUserProfile(currentUser.uid, {
        customPeriodisationTypes: updatedTypes,
      });
      await refreshUserData();
      if (userData) {
        setUserData({ ...userData, customPeriodisationTypes: updatedTypes });
      }
    } catch (error) {
      console.error("Error updating custom periodisation types:", error);
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update custom periodisation types",
      );
    } finally {
      setSavingCustomPeriodisation(false);
    }
  };

  const handleAddCustomPeriodisation = async () => {
    const trimmed = newCustomPeriodisationName.trim();
    if (!trimmed) {
      setCustomPeriodisationError("Periodisation name cannot be empty");
      return;
    }
    if (trimmed.length > 40) {
      setCustomPeriodisationError("Name must be 40 characters or less");
      return;
    }
    const current = userData?.customPeriodisationTypes || [];
    if (current.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setCustomPeriodisationError("This periodisation type already exists");
      return;
    }
    if (current.length >= 20) {
      setCustomPeriodisationError(
        "Maximum 20 custom periodisation types allowed",
      );
      return;
    }
    try {
      setCustomPeriodisationError(null);
      await updateCustomPeriodisationInFirebase([...current, trimmed]);
      setNewCustomPeriodisationName("");
      setIsAddingCustomPeriodisation(false);
    } catch (error) {
      setCustomPeriodisationError(
        error instanceof Error
          ? error.message
          : "Failed to add periodisation type",
      );
    }
  };

  const handleConfirmRemoveCustomPeriodisation = async () => {
    if (!customPeriodisationToDelete) return;
    const current = userData?.customPeriodisationTypes || [];
    try {
      await updateCustomPeriodisationInFirebase(
        current.filter((t) => t !== customPeriodisationToDelete),
      );
      setCustomPeriodisationToDelete(null);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to remove periodisation type",
      );
      setCustomPeriodisationToDelete(null);
    }
  };

  const handleSubmitCustomPeriodisation = (e: React.FormEvent) => {
    e.preventDefault();
    handleAddCustomPeriodisation();
  };

  const handleCancelAddCustomPeriodisation = () => {
    setIsAddingCustomPeriodisation(false);
    setNewCustomPeriodisationName("");
    setCustomPeriodisationError(null);
  };

  // ─── Custom Training Types handlers ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    trainingTypeService
      .fetchTrainingTypesForSports(userData?.trainingSportIDs)
      .then((types) => {
        if (cancelled) return;
        // Catalogue only — exclude any previously merged customs.
        setCatalogueTrainingTypes(
          types.filter((t) => !t.isCustomTrainingType),
        );
        if (!newCustomTrainingTypeBaseID && types.length > 0) {
          const first = types.find((t) => t.id && !t.isCustomTrainingType);
          if (first?.id) setNewCustomTrainingTypeBaseID(first.id);
        }
      })
      .catch(() => {
        if (!cancelled) setCatalogueTrainingTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userData?.trainingSportIDs]);

  const updateCustomTrainingTypesInFirebase = async (
    updatedTypes: CustomTrainingType[],
  ) => {
    if (!currentUser?.uid) return;
    try {
      setSavingCustomTrainingTypes(true);
      await userService.updateUserProfile(currentUser.uid, {
        customTrainingTypes: updatedTypes,
      });
      await refreshUserData();
      if (userData) {
        setUserData({ ...userData, customTrainingTypes: updatedTypes });
      }
    } catch (error) {
      console.error("Error updating custom training types:", error);
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update custom training types",
      );
    } finally {
      setSavingCustomTrainingTypes(false);
    }
  };

  const handleAddCustomTrainingType = async () => {
    const trimmed = newCustomTrainingTypeName.trim();
    if (!trimmed) {
      setCustomTrainingTypeError("Training type name cannot be empty");
      return;
    }
    if (trimmed.length > 50) {
      setCustomTrainingTypeError("Name must be 50 characters or less");
      return;
    }
    if (!newCustomTrainingTypeBaseID) {
      setCustomTrainingTypeError("Select a training type to base this on");
      return;
    }
    const current = userData?.customTrainingTypes || [];
    if (current.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      setCustomTrainingTypeError("This custom training type already exists");
      return;
    }
    if (
      catalogueTrainingTypes.some(
        (t) => t.name.trim().toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setCustomTrainingTypeError(
        "A catalogue training type already uses this name",
      );
      return;
    }
    if (current.length >= 20) {
      setCustomTrainingTypeError("Maximum 20 custom training types allowed");
      return;
    }
    const base = catalogueTrainingTypes.find(
      (t) => t.id === newCustomTrainingTypeBaseID,
    );
    if (!base?.id) {
      setCustomTrainingTypeError("Select a valid base training type");
      return;
    }
    try {
      setCustomTrainingTypeError(null);
      const next: CustomTrainingType = {
        id: `custom_${Date.now()}`,
        name: trimmed,
        relatedTrainingTypeID: base.id,
        relatedTrainingTypeName: base.name,
        createdAt: Date.now(),
      };
      await updateCustomTrainingTypesInFirebase([...current, next]);
      setNewCustomTrainingTypeName("");
      setIsAddingCustomTrainingType(false);
    } catch (error) {
      setCustomTrainingTypeError(
        error instanceof Error
          ? error.message
          : "Failed to add custom training type",
      );
    }
  };

  const handleConfirmRemoveCustomTrainingType = async () => {
    if (!customTrainingTypeToDelete) return;
    const current = userData?.customTrainingTypes || [];
    try {
      await updateCustomTrainingTypesInFirebase(
        current.filter((t) => t.id !== customTrainingTypeToDelete.id),
      );
      setCustomTrainingTypeToDelete(null);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to remove custom training type",
      );
      setCustomTrainingTypeToDelete(null);
    }
  };

  const handleSubmitCustomTrainingType = (e: React.FormEvent) => {
    e.preventDefault();
    handleAddCustomTrainingType();
  };

  const handleCancelAddCustomTrainingType = () => {
    setIsAddingCustomTrainingType(false);
    setNewCustomTrainingTypeName("");
    setCustomTrainingTypeError(null);
  };

  // Handle sport request dialog
  const handleOpenSportRequestDialog = () => {
    setShowSportRequestDialog(true);
  };

  const handleCloseSportRequestDialog = () => {
    setShowSportRequestDialog(false);
  };

  const handleGoToFeedback = () => {
    navigate("/updates");
  };
  const allPacksAdded =
  globalCategories.length > 0 &&
  globalCategories.every((c) =>
    (userData?.adminActivityCatIDs || []).includes(c.id || ""),
  );
  if (loading) {
    return (
      <div className="w-full pt-16">
        <Header />
        <div className="container max-w-2xl mx-auto py-8 px-8">
          {/* Header */}
          <div className="flex items-center mb-2 pb-4">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors mr-4"
            >
              <HiArrowLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content">
              Athlete Preferences
            </h1>
          </div>
          <div className="animate-pulse">
            <div className="h-8 bg-base-300 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              <div className="h-6 bg-base-300 rounded"></div>
              <div className="h-6 bg-base-300 rounded"></div>
              <div className="h-6 bg-base-300 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full pt-16">
        <Header />
        <div className="container max-w-2xl mx-auto py-8 px-8">
          {/* Header */}
          <div className="flex items-center mb-2 pb-4">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors mr-4"
            >
              <HiArrowLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content">
              Athlete Preferences
            </h1>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-600">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 pb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors mr-4"
            >
              <HiArrowLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content">
              Athlete Preferences
            </h1>
          </div>
        </div>

        {/* Casual Mode Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-semibold text-base-content">Mode</h3>
          </div>

          <div className="bg-base-300 border border-base-300 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  htmlFor="casual-mode-toggle"
                  className="block text-sm font-bold text-base-content mb-1"
                >
                  Simple Mode
                </label>
                <p className="text-xs">
                  Disable Wellness Logs, Health and Load Traicking tracking from
                  dashboard. Just use a digital training Diary.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    id="casual-mode-toggle"
                    type="checkbox"
                    checked={userData?.casualMode || false}
                    onChange={(e) => handleCasualModeChange(e.target.checked)}
                    disabled={savingCasualMode}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-base-100 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-neutral after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                </label>
              </div>
            </div>
            {savingCasualMode && (
              <div className="text-sm text-base-content/60 italic mt-2">
                Saving changes...
              </div>
            )}
          </div>

          {/* Hide peak score */}
          <div className="bg-base-300 border border-base-300 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  htmlFor="hide-peak-score-toggle"
                  className="block text-sm font-bold text-base-content mb-1"
                >
                  Hide peak score
                </label>
                <p className="text-xs">
                  Hide the Peak Readiness score from your dashboard. Useful
                  during competitions.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    id="hide-peak-score-toggle"
                    type="checkbox"
                    checked={userData?.hidePeakScore || false}
                    onChange={(e) =>
                      handleHidePeakScoreChange(e.target.checked)
                    }
                    disabled={savingHidePeakScore}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-base-100 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-neutral after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                </label>
              </div>
            </div>
            {savingHidePeakScore && (
              <div className="text-sm text-base-content/60 italic mt-2">
                Saving changes...
              </div>
            )}
          </div>

          {/* Hide wellness and sleep */}
          <div className="bg-base-300 border border-base-300 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  htmlFor="hide-wellness-sleep-toggle"
                  className="block text-sm font-bold text-base-content mb-1"
                >
                  Hide wellness and sleep
                </label>
                <p className="text-xs">
                  Hide the Wellness and Sleep rings from your dashboard.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    id="hide-wellness-sleep-toggle"
                    type="checkbox"
                    checked={userData?.hideWellnessSleep || false}
                    onChange={(e) =>
                      handleHideWellnessSleepChange(e.target.checked)
                    }
                    disabled={savingHideWellnessSleep}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-base-100 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-neutral after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                </label>
              </div>
            </div>
            {savingHideWellnessSleep && (
              <div className="text-sm text-base-content/60 italic mt-2">
                Saving changes...
              </div>
            )}
          </div>
        </div>

        {/* Training Sport Selection Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-base-content">Sports</h3>
          </div>

          <p className="text-base-content/70 text-sm mb-4">
            Select your training sports to access sport-specific session types
            and load modifiers.
          </p>

          {loadingSports ? (
            <div className="bg-base-100 border border-base-300 rounded-lg p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-base-300 rounded w-1/4 mb-2"></div>
                <div className="h-10 bg-base-300 rounded w-full"></div>
              </div>
            </div>
          ) : Object.keys(availableSports).length > 0 ? (
            <div className="bg-base-300 border border-base-300 rounded-lg p-4">
              <label className="block text-sm font-bold text-base-content">
                Choose your sport
              </label>

              <p className="text-xs text-base-content/60 mb-2">
                Multiple sports is available
              </p>

              {/* Sport selector button */}
              <button
                type="button"
                onClick={() => setSportPopupOpen(true)}
                disabled={loadingSports}
                className="w-full p-3 border bg-base-100 border-base-300 rounded-lg text-left flex items-center justify-between hover:bg-base-200 transition-colors"
              >
                <span className="truncate text-base-content">
                  {selectedSports.length > 0
                    ? selectedSports
                        .map((id) => availableSports[id])
                        .filter(Boolean)
                        .join(", ")
                    : "Select sports"}
                </span>
                <HiChevronRight className="text-base-content/60 flex-shrink-0 ml-2" />
              </button>

              {/* <div className="mt-4 text-center">
                <button
                  onClick={handleOpenSportRequestDialog}
                  className="text-sm text-base-content/70 hover:text-base-content underline"
                >
                  Can't find my sport?
                </button>
              </div> */}
            </div>
          ) : (
            <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
              <HiTrophy className="mx-auto text-base-content/50 text-3xl mb-3" />
              <h4 className="text-base-content/80 font-medium mb-2">
                No Sports Available
              </h4>
              <p className="text-base-content/60 text-sm mb-4">
                No sports have been configured yet. Contact your administrator
                to add sports.
              </p>
              <button
                onClick={handleOpenSportRequestDialog}
                className="text-sm text-base-content/70 hover:text-base-content underline"
              >
                Can't find my sport?
              </button>
            </div>
          )}
        </div>

        {/* Simple Wellness Setting Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-base-content">
              Wellness Log Preferences
            </h3>
          </div>

          <p className="text-base-content/70 text-sm mb-4">
            Customize how you log your daily wellness check-ins.
          </p>

          <div className="bg-base-300 border border-base-300 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  htmlFor="simple-wellness-toggle"
                  className="block text-sm font-bold text-base-content mb-1"
                >
                  Simple Mode
                </label>
                <p className="text-xs text-base-content/60">
                  Enable simple mode to log wellness with just sliders and
                  minimal text. Perfect for quick daily check-ins.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    id="simple-wellness-toggle"
                    type="checkbox"
                    checked={userData?.simpleWellnessSetting || false}
                    onChange={(e) =>
                      handleSimpleWellnessSettingChange(e.target.checked)
                    }
                    disabled={savingWellnessSetting}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-base-100 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-neutral after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                </label>
              </div>
            </div>
            {savingWellnessSetting && (
              <div className="text-sm text-base-content/60 italic mt-2">
                Saving changes...
              </div>
            )}
          </div>
        </div>

        {/* Custom Periodisation Types Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-base-content">
                Custom Periodisation
              </h3>
            </div>
          </div>

          <p className="text-base-content/70 text-sm mb-6">
            Add your own periodisation phases to use when planning sessions and
            programs.
          </p>

          <button
            onClick={() => setIsAddingCustomPeriodisation(true)}
            disabled={savingCustomPeriodisation}
            className="flex w-full mb-6 font-grotesk justify-center text-center items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary border border-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiPlus className="text-sm" />
            Add Custom Phase
          </button>

          {/* Display Custom Periodisation Types */}
          {userData?.customPeriodisationTypes &&
          userData.customPeriodisationTypes.length > 0 ? (
            <div className="space-y-4">
              <div className="grid gap-3">
                {userData.customPeriodisationTypes
                  .sort((a, b) => a.localeCompare(b))
                  .map((phase) => (
                    <div
                      key={phase}
                      className="flex items-center justify-between p-3 bg-base-300 border border-base-300 rounded-lg"
                    >
                      <h4 className="font-bold text-base-content">{phase}</h4>
                      <button
                        onClick={() => setCustomPeriodisationToDelete(phase)}
                        disabled={savingCustomPeriodisation}
                        className="p-1 flex items-center text-base-content/50 hover:text-red-600 hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={`Remove "${phase}"`}
                      >
                        <HiXMark className="text-sm" />
                      </button>
                    </div>
                  ))}
              </div>

              {savingCustomPeriodisation && (
                <div className="text-sm text-base-content/60 italic">
                  Saving changes...
                </div>
              )}
            </div>
          ) : (
            <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
              <HiCog6Tooth className="mx-auto text-base-content/50 text-3xl mb-3" />
              <h4 className="text-base-content/80 font-medium mb-2">
                No Custom Periodisation Phases
              </h4>
              <p className="text-base-content/60 text-sm mb-4">
                Create your first custom periodisation phase to personalize your
                training planning.
              </p>
              <button
                onClick={() => setIsAddingCustomPeriodisation(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-base-content/70 bg-base-200 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <HiPlus className="text-sm" />
                Add Your First Custom Phase
              </button>
            </div>
          )}
        </div>

        {/* Schedule Colours Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-base-content">
                Schedule Colours
              </h3>
            </div>
            {savingColors && (
              <span className="text-sm text-base-content/60 italic">
                Saving...
              </span>
            )}
          </div>

          <p className="text-base-content/70 text-sm mb-6">
            Personalise the colours used across your Schedule — training-type
            dots on session cards and periodisation phases in the Year view.
          </p>

          {/* Training type colours */}
          <h4 className="text-sm font-semibold text-base-content mb-3">
            Training types
          </h4>
          {catalogueTrainingTypes.length === 0 ? (
            <p className="text-sm text-base-content/60 mb-6">
              Select a sport above to customise training-type colours.
            </p>
          ) : (
            <div className="grid gap-2 mb-6">
              {catalogueTrainingTypes
                .filter((t) => t.id)
                .map((type) => {
                  const key = type.id as string;
                  const current = resolveTrainingTypeColor(
                    key,
                    userData?.trainingTypeColors,
                  );
                  return (
                    <label
                      key={key}
                      className="flex items-center justify-between gap-3 p-3 bg-base-300 border border-base-300 rounded-lg cursor-pointer"
                    >
                      <span className="flex items-center gap-2.5">
                        <span
                          className="h-4 w-4 rounded-full"
                          style={{ backgroundColor: current }}
                        />
                        <span className="font-medium text-base-content">
                          {type.name}
                        </span>
                      </span>
                      <input
                        type="color"
                        defaultValue={current}
                        disabled={savingColors}
                        onBlur={(e) =>
                          updateColorPreference(
                            "trainingTypeColors",
                            key,
                            e.target.value,
                          )
                        }
                        className="h-8 w-12 cursor-pointer rounded border border-base-300 bg-transparent"
                        aria-label={`Colour for ${type.name}`}
                      />
                    </label>
                  );
                })}
            </div>
          )}

          {/* Periodisation colours */}
          <h4 className="text-sm font-semibold text-base-content mb-3">
            Periodisation phases
          </h4>
          <div className="grid gap-2">
            {[
              ...PERIODISATION_OPTIONS.map((o) => ({
                key: o.value,
                label: o.label,
              })),
              ...(userData?.customPeriodisationTypes || []).map((p) => ({
                key: p,
                label: p,
              })),
            ].map(({ key, label }) => {
              const current = resolvePeriodisationColor(
                key,
                userData?.periodisationColors,
              );
              return (
                <label
                  key={key}
                  className="flex items-center justify-between gap-3 p-3 bg-base-300 border border-base-300 rounded-lg cursor-pointer"
                >
                  <span className="flex items-center gap-2.5">
                    <span
                      className="h-4 w-4 rounded-full"
                      style={{ backgroundColor: current }}
                    />
                    <span className="font-medium text-base-content">
                      {label}
                    </span>
                  </span>
                  <input
                    type="color"
                    defaultValue={current}
                    disabled={savingColors}
                    onBlur={(e) =>
                      updateColorPreference(
                        "periodisationColors",
                        key,
                        e.target.value,
                      )
                    }
                    className="h-8 w-12 cursor-pointer rounded border border-base-300 bg-transparent"
                    aria-label={`Colour for ${label}`}
                  />
                </label>
              );
            })}
          </div>
        </div>

        {/* Custom Training Types Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-base-content">
                Custom Training Types
              </h3>
            </div>
          </div>

          <p className="text-base-content/70 text-sm mb-6">
            Create your own training types based on a catalogue type. Custom
            types inherit that type&apos;s load modifier and appear in your
            training type pickers.
          </p>

          <button
            onClick={() => setIsAddingCustomTrainingType(true)}
            disabled={
              savingCustomTrainingTypes || catalogueTrainingTypes.length === 0
            }
            className="flex w-full mb-6 font-grotesk justify-center text-center items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary border border-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiPlus className="text-sm" />
            Add Custom Training Type
          </button>

          {catalogueTrainingTypes.length === 0 && (
            <p className="text-sm text-base-content/60 mb-4">
              Select a sport above to choose a base training type.
            </p>
          )}

          {userData?.customTrainingTypes &&
          userData.customTrainingTypes.length > 0 ? (
            <div className="space-y-4">
              <div className="grid gap-3">
                {[...userData.customTrainingTypes]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((type) => (
                    <div
                      key={type.id}
                      className="flex items-center justify-between p-3 bg-base-300 border border-base-300 rounded-lg"
                    >
                      <div>
                        <h4 className="font-bold text-base-content">
                          {type.name}
                        </h4>
                        <p className="text-xs text-base-content/60 mt-0.5">
                          Based on {type.relatedTrainingTypeName}
                        </p>
                      </div>
                      <button
                        onClick={() => setCustomTrainingTypeToDelete(type)}
                        disabled={savingCustomTrainingTypes}
                        className="p-1 flex items-center text-base-content/50 hover:text-red-600 hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={`Remove "${type.name}"`}
                      >
                        <HiXMark className="text-sm" />
                      </button>
                    </div>
                  ))}
              </div>

              {savingCustomTrainingTypes && (
                <div className="text-sm text-base-content/60 italic">
                  Saving changes...
                </div>
              )}
            </div>
          ) : (
            <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
              <HiTrophy className="mx-auto text-base-content/50 text-3xl mb-3" />
              <h4 className="text-base-content/80 font-medium mb-2">
                No Custom Training Types
              </h4>
              <p className="text-base-content/60 text-sm mb-4">
                Create a custom training type based on a catalogue type to use
                when logging training and planning sessions.
              </p>
            </div>
          )}
        </div>
        {/* Activity Packs Section */}
       {/* Activity Packs Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-base-content">
                Activity Packs
              </h3>
            </div>
            {globalCategories.length > 0 && !allPacksAdded && (
              <button
                onClick={handleAddAllPacks}
                disabled={addingAllPacks || togglingPackId !== null}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                {addingAllPacks ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    Adding All...
                  </>
                ) : (
                  <>
                    <HiPlus className="text-sm" />
                    Add All Packs
                  </>
                )}
              </button>
            )}
          </div>

          <p className="text-base-content/70 text-sm mb-6">
            Add pre-built exercise packs to your library. Toggle on the sports
            relevant to you to see their exercises in the Exercises page and
            session builder.
          </p>

          {packToggleError && (
            <div className="text-red-600 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-4">
              {packToggleError}
            </div>
          )}

          {loadingGlobalCategories ? (
            <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
              <div className="animate-pulse space-y-3">
                <div className="h-6 bg-base-300 rounded"></div>
                <div className="h-6 bg-base-300 rounded"></div>
                <div className="h-6 bg-base-300 rounded"></div>
              </div>
            </div>
          ) : globalCategories.length > 0 ? (
            <div className="space-y-3">
              <div className={`grid gap-3 transition-opacity ${addingAllPacks ? "opacity-50 pointer-events-none" : ""}`}>
                {globalCategories
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((category) => {
                    const isAdded = (userData?.adminActivityCatIDs || []).includes(
                      category.id || "",
                    );
                    const isToggling = togglingPackId === category.id;

                    return (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 bg-base-300 border border-base-300 rounded-lg"
                      >
                        <div>
                          <h4 className="font-bold text-base-content">
                            {category.name}
                          </h4>
                          {category.description && (
                            <p className="text-xs text-base-content/60 mt-0.5">
                              {category.description}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => category.id && handleTogglePack(category.id)}
                          disabled={isToggling || addingAllPacks}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isAdded
                              ? "text-red-600 bg-red-500/10 hover:bg-red-500/15 border border-red-500/30"
                              : "text-white bg-primary hover:bg-primary/90 border border-primary"
                          }`}
                        >
                          {isToggling
                            ? "..."
                            : isAdded
                              ? "Remove"
                              : "Add"}
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : (
            <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
              <HiSquares2X2 className="mx-auto text-base-content/50 text-3xl mb-3" />
              <h4 className="text-base-content/80 font-medium mb-2">
                No Activity Packs Available
              </h4>
              <p className="text-base-content/60 text-sm">
                No packs have been configured yet. Check back later.
              </p>
            </div>
          )}
        </div>

        {/* Activity Types Section - Hidden for now */}
        {false && (
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-base-content">
                  Exercises
                </h3>
              </div>
              {!isAddingActivityType && (
                <button
                  onClick={() => setIsAddingActivityType(true)}
                  disabled={savingActivityTypes}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <HiPlus className="text-sm" />
                  Add Exercise
                </button>
              )}
            </div>

            <p className="text-base-content/70 text-sm mb-4">
              Create custom exercises with specific variables for tracking
              your workouts.
            </p>

            {/* Add Activity Type Form */}
            {isAddingActivityType && (
              <div className="mb-6 p-4 bg-base-100 border border-base-300 rounded-lg">
                <form onSubmit={handleActivityTypeSubmit} className="space-y-4">
                  {/* Activity Type Name */}
                  <div>
                    <label
                      htmlFor="activityTypeName"
                      className="block text-sm font-medium text-base-content/80 mb-2"
                    >
                      Exercise Name
                    </label>
                    <input
                      id="activityTypeName"
                      type="text"
                      value={newActivityType.name}
                      onChange={(e) => {
                        setNewActivityType({
                          ...newActivityType,
                          name: e.target.value,
                        });
                        setActivityTypeError(null);
                      }}
                      placeholder="e.g., Running, Swimming, Cycling"
                      className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-0 focus:border-transparent border-base-300 border-2 p-4 rounded-md"
                      disabled={savingActivityTypes}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label
                      htmlFor="activityTypeDescription"
                      className="block text-sm font-medium text-base-content/80 mb-2"
                    >
                      Description
                    </label>
                    <textarea
                      id="activityTypeDescription"
                      value={newActivityType.description}
                      onChange={(e) => {
                        setNewActivityType({
                          ...newActivityType,
                          description: e.target.value,
                        });
                        setActivityTypeError(null);
                      }}
                      placeholder="Enter a description for this exercise..."
                      rows={3}
                      className="textarea bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-0 focus:border-transparent border-base-300 border-2 p-4 rounded-md resize-none"
                      disabled={savingActivityTypes}
                      required
                    />
                  </div>

                  {/* Activity Variables */}
                  <div>
                    <label className="block text-sm font-medium text-base-content/80 mb-2">
                      Variables (Select all that apply)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {AVAILABLE_VARIABLES.map((variableName) => {
                        const selected = isVariableSelected(variableName);
                        const availableUnits =
                          getUnitsForVariable(variableName);
                        const currentUnit = getVariableUnit(variableName);

                        return (
                          <div
                            key={variableName}
                            className={`p-3 rounded-lg border-2 transition-colors ${
                              selected
                                ? "border-base-content bg-base-200"
                                : "border-base-300 bg-base-100"
                            }`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleVariable(variableName)}
                                disabled={savingActivityTypes}
                                className="w-4 h-4 text-blue-600 border-base-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm font-medium text-base-content">
                                {variableName}
                              </span>
                            </label>

                            {selected && (
                              <div className="ml-6">
                                <label className="text-xs text-base-content/70 font-grotesk mb-1 block">
                                  Default Unit:
                                </label>
                                <select
                                  value={currentUnit}
                                  onChange={(e) =>
                                    updateVariableUnit(
                                      variableName,
                                      e.target.value,
                                    )
                                  }
                                  disabled={savingActivityTypes}
                                  className="text-xs w-full px-2 py-1 border border-base-300 rounded bg-base-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  {availableUnits.map((unit) => (
                                    <option key={unit} value={unit}>
                                      {unit}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {newActivityType.variables.length === 0 && (
                      <p className="text-xs text-base-content/60 mt-2">
                        Please select at least one variable
                      </p>
                    )}
                  </div>

                  {/* Image URL (Optional) */}
                  <div>
                    <label
                      htmlFor="activityTypeImage"
                      className="block text-sm font-medium text-base-content/80 mb-2"
                    >
                      Image URL (Optional)
                    </label>
                    <input
                      id="activityTypeImage"
                      type="url"
                      value={newActivityType.image}
                      onChange={(e) =>
                        setNewActivityType({
                          ...newActivityType,
                          image: e.target.value,
                        })
                      }
                      placeholder="https://example.com/image.jpg"
                      className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-0 focus:border-transparent border-base-300 border-2 p-4 rounded-md"
                      disabled={savingActivityTypes}
                    />
                  </div>

                  {activityTypeError && (
                    <div className="text-red-600 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                      {activityTypeError}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={savingActivityTypes}
                      className="bg-neutral text-white py-3 px-6 font-medium rounded-md disabled:bg-base-content/40 disabled:cursor-not-allowed transition-colors"
                    >
                      {savingActivityTypes
                        ? editingActivityType
                          ? "Updating..."
                          : "Adding..."
                        : editingActivityType
                          ? "Update Exercise"
                          : "Add Exercise"}
                    </button>
                    <button
                      type="button"
                      onClick={resetActivityTypeForm}
                      disabled={savingActivityTypes}
                      className="px-4 py-2 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Display Activity Types */}
            {activityTypes.length > 0 ? (
              <div className="space-y-3">
                {activityTypes.map((activityType) => {
                  return (
                    <div
                      key={activityType.id}
                      className="bg-base-100 border border-base-300 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-base-content">
                              {activityType.name}
                            </h4>
                          </div>
                          <div className="text-sm text-base-content/70 space-y-1">
                            <p>
                              <strong>Variables:</strong>{" "}
                              {activityType.variables &&
                              activityType.variables.length > 0 ? (
                                activityType.variables.join(", ")
                              ) : (
                                <span className="text-base-content/50 italic">
                                  No variables
                                </span>
                              )}
                            </p>
                            {activityType.image && (
                              <p>
                                <strong>Image:</strong>{" "}
                                <span className="text-blue-600">Provided</span>
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditActivityType(activityType)}
                            disabled={savingActivityTypes}
                            className="p-2 text-blue-600 hover:bg-blue-500/10 rounded-full transition-colors disabled:opacity-50"
                            title="Edit exercise"
                          >
                            <HiPencil className="text-sm" />
                          </button>

                          <button
                            onClick={() =>
                              setActivityTypeToDelete(activityType)
                            }
                            disabled={savingActivityTypes}
                            className="p-2 text-red-600 hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50"
                            title="Delete exercise"
                          >
                            <HiTrash className="text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {savingActivityTypes && (
                  <div className="text-sm text-base-content/60 italic">
                    Saving changes...
                  </div>
                )}
              </div>
            ) : !isAddingActivityType ? (
              <div className="bg-base-100 border border-base-300 rounded-lg p-6 text-center">
                <HiSquares2X2 className="mx-auto text-base-content/50 text-3xl mb-3" />
                <h4 className="text-base-content/80 font-medium mb-2">
                  No Exercises
                </h4>
                <p className="text-base-content/60 text-sm mb-4">
                  Create your first exercise to customize your workout
                  tracking.
                </p>
                <button
                  onClick={() => setIsAddingActivityType(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/15 transition-colors"
                >
                  <HiPlus className="text-sm" />
                  Add Your First Exercise
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Sport Request Dialog */}
      {showSportRequestDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Request Your Sport
            </h3>
            <p className="text-base-content/70 mb-6">
              If your sport is not in there, please request it in the feedback
              page and we will get back to you asap!
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseSportRequestDialog}
                className="px-4 py-2 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleGoToFeedback}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Type Delete Confirmation Dialog */}
      {activityTypeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Delete Exercise
            </h3>
            <p className="text-base-content/70 mb-6">
              Are you sure you want to delete the exercise{" "}
              <strong>"{activityTypeToDelete.name}"</strong>? This will hide it
              from future use, but existing sessions and measurements will
              remain intact.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setActivityTypeToDelete(null)}
                disabled={savingActivityTypes}
                className="px-4 py-2 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteActivityType(activityTypeToDelete)}
                disabled={savingActivityTypes}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingActivityTypes ? "Deleting..." : "Delete Exercise"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sport Selection Popup */}
      {sportPopupOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/40 sm:flex sm:backdrop-blur-sm sm:items-center sm:justify-center">
          <div className="bg-base-100 w-full h-full sm:h-auto sm:max-w-md sm:rounded-2xl rounded-none p-6 sm:p-8 sm:max-h-[70vh] flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-2xl font-semibold text-left">
                Select Sports
              </h3>
              <button
                onClick={() => setSportPopupOpen(false)}
                className="text-base-content/60 hover:text-base-content/80 text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <p className="text-sm text-base-content/60 text-left mb-4">
              {selectedSports.length > 0
                ? `${selectedSports.length} sport${selectedSports.length !== 1 ? "s" : ""} selected`
                : "Choose one or more sports"}
            </p>

            <div className="flex-1 overflow-y-auto space-y-0 sm:space-y-2 -mx-6 px-8 sm:mx-0 sm:px-0">
              {Object.entries(availableSports).map(([id, name]) => {
                const checked = selectedSports.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      const next = checked
                        ? selectedSports.filter((s) => s !== id)
                        : [...selectedSports, id];
                      setSelectedSports(next);
                    }}
                    className={`w-full p-4 border-b sm:border sm:rounded-lg rounded-none flex justify-between items-center text-left transition-colors ${
                      checked
                        ? "border-primary bg-primary/10 text-primary font-semibold sm:border-primary"
                        : "border-base-300 sm:border-base-300"
                    }`}
                  >
                    <span className="font-medium text-left">{name}</span>
                    {checked && (
                      <svg
                        className="w-5 h-5 text-primary flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="pt-4 -mx-6 px-6 sm:mx-0 sm:px-0">
              <button
                className="w-full bg-primary text-white py-3 rounded-none sm:rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={savingSport}
                onClick={async () => {
                  // Save the selected sports
                  if (selectedSports.length > 0) {
                    await handleTrainingSportChange(selectedSports);
                    setSportPopupOpen(false);
                  } else {
                    setError("Please select at least one sport.");
                  }
                }}
              >
                {savingSport ? "Saving..." : "Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Periodisation Modal */}
      {isAddingCustomPeriodisation && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex backdrop-blur-sm items-center justify-center p-4">
          <div className="bg-base-100 w-full max-w-md rounded-2xl p-8 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold font-grotesk">
                Add Custom Periodisation Phase
              </h3>
              <button
                onClick={handleCancelAddCustomPeriodisation}
                disabled={savingCustomPeriodisation}
                className="text-base-content/60 hover:text-base-content/80 text-3xl leading-none disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSubmitCustomPeriodisation}
              className="flex-1 overflow-y-auto space-y-4"
            >
              <div className="form-control">
                <div className="new-form-group-rounded w-full">
                  <input
                    id="newCustomPeriodisationName"
                    type="text"
                    className="form-element-rounded"
                    value={newCustomPeriodisationName}
                    onChange={(e) => {
                      setNewCustomPeriodisationName(e.target.value);
                      setCustomPeriodisationError(null);
                    }}
                    placeholder="e.g., Peaking, Base Building, GPP"
                    maxLength={40}
                    disabled={savingCustomPeriodisation}
                    autoFocus
                  />
                  <label>Phase Name</label>
                </div>
                <div className="flex justify-between items-center mt-1 pl-4">
                  <span className="text-xs text-base-content/60">
                    {newCustomPeriodisationName.length}/40 characters
                  </span>
                </div>
              </div>

              {customPeriodisationError && (
                <div className="text-red-600 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                  {customPeriodisationError}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={
                    savingCustomPeriodisation ||
                    !newCustomPeriodisationName.trim()
                  }
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-grotesk font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingCustomPeriodisation ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Custom Periodisation */}
      {customPeriodisationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Remove Custom Periodisation Phase
            </h3>
            <p className="text-base-content/70 mb-6">
              Are you sure you want to remove{" "}
              <strong>"{customPeriodisationToDelete}"</strong>? This action
              cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCustomPeriodisationToDelete(null)}
                disabled={savingCustomPeriodisation}
                className="px-4 py-2 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemoveCustomPeriodisation}
                disabled={savingCustomPeriodisation}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCustomPeriodisation ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Training Type Dialog */}
      {isAddingCustomTrainingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-base-content">
                Add Custom Training Type
              </h3>
              <button
                onClick={handleCancelAddCustomTrainingType}
                className="p-1 text-base-content/50 hover:text-base-content rounded-full"
              >
                <HiXMark />
              </button>
            </div>
            <form
              onSubmit={handleSubmitCustomTrainingType}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-base-content mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newCustomTrainingTypeName}
                  onChange={(e) => setNewCustomTrainingTypeName(e.target.value)}
                  placeholder="e.g. Tempo intervals"
                  maxLength={50}
                  className="w-full px-3 py-2 border border-base-300 rounded-lg bg-base-100 text-base-content"
                  autoFocus
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-base-content/60">
                    {newCustomTrainingTypeName.length}/50 characters
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-base-content mb-1">
                  Based on
                </label>
                <select
                  value={newCustomTrainingTypeBaseID}
                  onChange={(e) =>
                    setNewCustomTrainingTypeBaseID(e.target.value)
                  }
                  className="w-full px-3 py-2 border border-base-300 rounded-lg bg-base-100 text-base-content"
                >
                  {catalogueTrainingTypes
                    .filter((t) => t.id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-base-content/60 mt-1">
                  Your custom type inherits this type&apos;s load modifier.
                </p>
              </div>

              {customTrainingTypeError && (
                <div className="text-red-600 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                  {customTrainingTypeError}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCancelAddCustomTrainingType}
                  className="flex-1 px-4 py-3 text-sm font-medium text-base-content/70 bg-base-200 border border-base-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    savingCustomTrainingTypes ||
                    !newCustomTrainingTypeName.trim() ||
                    !newCustomTrainingTypeBaseID
                  }
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-grotesk font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingCustomTrainingTypes ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Custom Training Type */}
      {customTrainingTypeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              Remove Custom Training Type
            </h3>
            <p className="text-base-content/70 mb-6">
              Are you sure you want to remove{" "}
              <strong>"{customTrainingTypeToDelete.name}"</strong>? Existing
              logs that used it will keep their saved type.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCustomTrainingTypeToDelete(null)}
                disabled={savingCustomTrainingTypes}
                className="px-4 py-2 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemoveCustomTrainingType}
                disabled={savingCustomTrainingTypes}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCustomTrainingTypes ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AthletePreferences;
